import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { DEFAULT_ORGANIZATION } from "src/grading/models";
import {
  Member,
  useGetOrganizationsQuery,
  useTransferAthleteMutation,
} from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import useStyles from "./OrgTransferModal.styles";

interface OrgTransferModalProps {
  member?: Member;
  fetchMembers: () => void;
  open: boolean;
  onClose: () => void;
}

export interface OrgTransferAthleteFormValues {
  organizationId: string;
}

const validationSchema = Yup.object().shape({
  organizationId: Yup.string().required("Organization Id is required"),
});

const OrgTransferModal = (props: OrgTransferModalProps) => {
  const classes = useStyles();

  const { data: organizationsData } = useGetOrganizationsQuery({});

  const [transferAthlete, { isLoading }] = useTransferAthleteMutation();

  const formik = useFormik({
    initialValues: {
      organizationId: "",
    },
    validationSchema,
    onSubmit: async (values: OrgTransferAthleteFormValues) => {
      if (props.member) {
        const result = await transferAthlete({
          memberId: props.member.memberId,
          organizationId: props.member.organizationId,
          newOrganizationId: values.organizationId,
        }).unwrap();

        if (result.success) {
          props.fetchMembers();
          props.onClose();
          formik.resetForm();
        }
      }
    },
  });

  const { values, setFieldValue, handleSubmit, errors } = formik;

  return (
    <RFModal
      open={props.open}
      boxProps={{ className: classes.container }}
      onClose={props.onClose}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <Typography variant="h5">Transfer Athlete</Typography>

        <Grid container item marginTop={theme.spacing(2)} spacing={2}>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>User Name</InputLabel>
            <TextField
              InputProps={{
                className: classes.input,
              }}
              variant="outlined"
              fullWidth
              value={`${props.member?.firstName} ${props.member?.lastName}`}
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>Organization</InputLabel>

            <Select
              className={classes.input}
              variant="outlined"
              fullWidth
              value={values.organizationId}
              onChange={(e) => setFieldValue("organizationId", e.target.value)}
            >
              {organizationsData?.organizations &&
              organizationsData?.organizations.length > 1 ? (
                organizationsData?.organizations?.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={DEFAULT_ORGANIZATION.id}>
                  {DEFAULT_ORGANIZATION.name}
                </MenuItem>
              )}
            </Select>

            {errors?.organizationId && (
              <FormHelperText
                style={{ marginLeft: 14, color: "red", fontSize: "14px" }}
                error
              >
                {errors?.organizationId}
              </FormHelperText>
            )}
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(4)}
        >
          <Grid item>
            <Button
              color="secondary"
              className={` ${classes.button} ${classes.backButton}`}
              onClick={props.onClose}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label="Save"
              type="submit"
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default OrgTransferModal;
